/**
 * Feedback Modal Component
 * Main interface for collecting detailed user feedback
 */

import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { 
  FeedbackType, 
  Feedback,
  FeedbackFormState,
  ElementSelectionState,
  FeedbackWidgetConfig,
  ScreenshotOptions
} from '../../types/feedback';
import FeedbackFormSelector from './forms/FeedbackFormSelector';
import QuickReactionForm from './forms/QuickReactionForm';
import PageCommentForm from './forms/PageCommentForm';
import ElementFeedbackForm from './forms/ElementFeedbackForm';
import BugReportForm from './forms/BugReportForm';
import FeatureRequestForm from './forms/FeatureRequestForm';
import GeneralFeedbackForm from './forms/GeneralFeedbackForm';
import FlowRatingForm from './forms/FlowRatingForm';

interface FeedbackModalProps {
  feedbackType: FeedbackType | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: Feedback) => Promise<void>;
  elementSelection: ElementSelectionState;
  onElementSelectionChange: (selection: ElementSelectionState) => void;
  config: FeedbackWidgetConfig;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  feedbackType: initialFeedbackType,
  isOpen,
  onClose,
  onSubmit,
  elementSelection,
  onElementSelectionChange,
  config
}) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(initialFeedbackType);
  const [formState, setFormState] = useState<FeedbackFormState>({
    currentStep: 1,
    totalSteps: 1,
    data: {},
    validation: { isValid: false, errors: {} },
    isSubmitting: false,
    isCapturingScreenshot: false,
    isSelectingElement: false
  });
  
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Reset form when modal opens/closes or feedback type changes
  useEffect(() => {
    if (isOpen && initialFeedbackType) {
      setFeedbackType(initialFeedbackType);
      setFormState({
        currentStep: 1,
        totalSteps: getStepsForFeedbackType(initialFeedbackType),
        data: {
          type: initialFeedbackType,
          timestamp: new Date(),
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          }
        },
        validation: { isValid: false, errors: {} },
        isSubmitting: false,
        isCapturingScreenshot: false,
        isSelectingElement: false
      });
    }
  }, [isOpen, initialFeedbackType]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !formState.isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, formState.isSubmitting]);

  // Handle click outside to close
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === overlayRef.current && !formState.isSubmitting) {
      onClose();
    }
  };

  const getStepsForFeedbackType = (type: FeedbackType): number => {
    switch (type) {
      case 'quick-reaction':
        return 1;
      case 'page-comment':
        return 2;
      case 'element-feedback':
        return 1;
      case 'bug-report':
        return 3;
      case 'feature-request':
        return 1;
      case 'flow-rating':
        return 2;
      case 'general':
        return 1;
      default:
        return 1;
    }
  };

  const handleFormDataChange = (data: Partial<Feedback>) => {
    setFormState(prev => ({
      ...prev,
      data: { ...prev.data, ...data },
      validation: validateFormData({ ...prev.data, ...data })
    }));
  };

  const validateFormData = (data: Partial<Feedback>) => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!data.type) {
      errors.type = 'Feedback type is required';
      isValid = false;
    }

    // Type-specific validation
    switch (data.type) {
      case 'page-comment':
        if (!(data as any).comment?.trim()) {
          errors.comment = 'Comment is required';
          isValid = false;
        }
        break;
      case 'element-feedback':
        if (!(data as any).feedbackText?.trim()) {
          errors.feedbackText = 'Feedback text is required';
          isValid = false;
        }
        break;
      case 'bug-report':
        if (!(data as any).title?.trim()) {
          errors.title = 'Title is required';
          isValid = false;
        }
        if (!(data as any).description?.trim()) {
          errors.description = 'Description is required';
          isValid = false;
        }
        break;
      case 'feature-request':
        if (!(data as any).title?.trim()) {
          errors.title = 'Title is required';
          isValid = false;
        }
        if (!(data as any).description?.trim()) {
          errors.description = 'Description is required';
          isValid = false;
        }
        if (!(data as any).useCase?.trim()) {
          errors.useCase = 'Use case is required';
          isValid = false;
        }
        break;
      case 'general':
        if (!(data as any).message?.trim()) {
          errors.message = 'Message is required';
          isValid = false;
        }
        break;
    }

    return { isValid, errors };
  };

  const handleSubmit = async () => {
    if (!formState.validation.isValid || formState.isSubmitting) {
      return;
    }

    setFormState(prev => ({ ...prev, isSubmitting: true }));

    try {
      await onSubmit(formState.data as Feedback);
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        validation: {
          ...prev.validation,
          errors: { submit: 'Failed to submit feedback. Please try again.' }
        }
      }));
    }
  };

  const handleBack = () => {
    if (formState.currentStep > 1) {
      setFormState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    } else if (feedbackType) {
      setFeedbackType(null);
    }
  };

  const handleNext = () => {
    if (formState.currentStep < formState.totalSteps) {
      setFormState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    }
  };

  const renderFeedbackForm = () => {
    if (!feedbackType) {
      return (
        <FeedbackFormSelector 
          onSelect={setFeedbackType}
          availableTypes={['page-comment', 'bug-report', 'feature-request', 'element-feedback', 'general']}
        />
      );
    }

    const commonProps = {
      data: formState.data,
      onChange: handleFormDataChange,
      validation: formState.validation,
      currentStep: formState.currentStep,
      totalSteps: formState.totalSteps
    };

    switch (feedbackType) {
      case 'quick-reaction':
        return <QuickReactionForm {...commonProps} />;
      case 'page-comment':
        return (
          <PageCommentForm 
            {...commonProps}
            enableScreenshot={config.enableScreenshot}
          />
        );
      case 'element-feedback':
        return (
          <ElementFeedbackForm 
            {...commonProps} 
            elementSelection={elementSelection}
            onElementSelectionChange={onElementSelectionChange}
            enableScreenshot={config.enableScreenshot}
          />
        );
      case 'bug-report':
        return (
          <BugReportForm 
            {...commonProps}
            enableScreenshot={config.enableScreenshot}
          />
        );
      case 'feature-request':
        return (
          <FeatureRequestForm 
            {...commonProps}
            enableScreenshot={config.enableScreenshot}
          />
        );
      case 'flow-rating':
        return <FlowRatingForm {...commonProps} />;
      case 'general':
        return (
          <GeneralFeedbackForm 
            {...commonProps}
            enableScreenshot={config.enableScreenshot}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={overlayRef}
      data-feedback-modal
      className="fixed inset-0 z-[999998] flex items-center justify-center p-4 bg-slate-900 bg-opacity-50"
      onClick={handleOverlayClick}
      style={{ zIndex: 999998 }}
    >
      <div 
        ref={modalRef}
        className={cn(
          'bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col',
          'animate-fade-in'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div className="flex items-center gap-4">
            {(feedbackType || formState.currentStep > 1) && (
              <button
                onClick={handleBack}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-stone-50"
                disabled={formState.isSubmitting}
                aria-label="Go back"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5"/>
                  <path d="M12 19l-7-7 7-7"/>
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-xl font-serif font-medium text-slate-800">
                {feedbackType ? getFeedbackTypeLabel(feedbackType) : 'Share Your Feedback'}
              </h2>
              {formState.totalSteps > 1 && (
                <p className="text-sm text-slate-600 mt-1">
                  Step {formState.currentStep} of {formState.totalSteps}
                </p>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-stone-50"
            disabled={formState.isSubmitting}
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18"/>
              <path d="M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        {formState.totalSteps > 1 && (
          <div className="px-6 pt-4">
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div 
                className="bg-accent-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(formState.currentStep / formState.totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {renderFeedbackForm()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-stone-200 bg-stone-50">
          <div className="text-sm text-slate-600">
            {formState.validation.errors.submit && (
              <p className="text-coral-600">{formState.validation.errors.submit}</p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {feedbackType && formState.currentStep < formState.totalSteps && (
              <button
                onClick={handleNext}
                disabled={!formState.validation.isValid || formState.isSubmitting}
                className="btn bg-accent-500 text-white hover:bg-accent-600 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed"
              >
                Next
              </button>
            )}
            
            {feedbackType && formState.currentStep === formState.totalSteps && (
              <button
                onClick={handleSubmit}
                disabled={!formState.validation.isValid || formState.isSubmitting}
                className="btn bg-accent-500 text-white hover:bg-accent-600 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {formState.isSubmitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r={10} stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25"/>
                      <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const getFeedbackTypeLabel = (type: FeedbackType): string => {
  const labels: Record<FeedbackType, string> = {
    'quick-reaction': 'Quick Reaction',
    'page-comment': 'Page Comment',
    'element-feedback': 'Element Feedback',
    'flow-rating': 'Flow Rating',
    'bug-report': 'Bug Report',
    'feature-request': 'Feature Request',
    'general': 'General Feedback'
  };
  return labels[type];
};

export default FeedbackModal;